import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Snowflake, Wrench, PackageSearch, RefreshCw, CalendarDays, Award, Truck } from 'lucide-react';
import { PartnerMapSection } from '../pages/PartnerLocations';
import { api } from '../lib/api';
import { money } from '../hooks/usePageContent';
import { useSiteTexts } from '../lib/siteTexts';
import Br from './Br';

const CATEGORIES = [
    { key: 'machines', icon: Snowflake, to: '/shop?type=machine' },
    { key: 'accessories', icon: PackageSearch, to: '/shop?type=accessory' },
    { key: 'used', icon: RefreshCw, to: '/shop?category=used' },
    { key: 'support', icon: Wrench, to: '/services' },
];

export const EquipmentCategories = () => {
    const t = useSiteTexts();
    return (
    <section className="container-page py-24 md:py-28" data-testid="home-equipment-categories">
        <div className="max-w-4xl mb-12">
            <div className="label-caps text-brand-primary mb-4">{t('home.cats.eyebrow')}</div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]">
                <Br text={t('home.cats.heading')} /><br /><span className="text-brand-primary">{t('home.cats.accent')}</span>
            </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map(({ key, icon: Icon, to }) => (
                <Link key={key} to={to} className="group border border-neutral-200 bg-white p-8 hover:border-brand-primary hover:-translate-y-1 transition-[border-color,transform] duration-300 flex flex-col" data-testid={`home-category-${key}`}>
                    <Icon className="h-8 w-8 text-brand-primary" />
                    <div className="mt-6 font-display font-black uppercase text-2xl text-neutral-900 leading-tight">{t(`home.cats.${key}.title`)}</div>
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed flex-1">{t(`home.cats.${key}.text`)}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 label-caps text-xs text-brand-primary">Browse <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></span>
                </Link>
            ))}
        </div>
    </section>
    );
};

const RENTAL_POINTS = [
    { icon: CalendarDays, text: 'Daily & weekly rates' },
    { icon: Award, text: 'Pickup, delivery & setup help' },
    { icon: Truck, text: 'Refundable deposit, no long contracts' },
];

const RentalMachines = () => {
    const [fleet, setFleet] = useState([]);
    useEffect(() => { api.get('/rentals/catalog').then((r) => setFleet(r.data.slice(0, 3))).catch(() => {}); }, []);
    if (!fleet.length) {
        return RENTAL_POINTS.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-4 border p-4" style={{ borderColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}>
                <Icon className="h-5 w-5 flex-shrink-0" style={{ color: '#F7941D' }} /> {text}
            </div>
        ));
    }
    return fleet.map((m) => (
        <Link key={m.id} to={`/rental?machine=${m.id}`} className="flex items-center gap-4 border p-3 hover:border-white/40 transition-colors" style={{ borderColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF' }} data-testid={`home-rental-machine-${m.id}`}>
            <span className="w-14 h-14 flex-shrink-0 bg-white">{m.image_url && <img src={m.image_url} alt={m.name} loading="lazy" className="w-full h-full object-contain p-1" />}</span>
            <span className="flex-1 min-w-0 font-bold truncate">{m.name}</span>
            <span className="label-caps text-xs" style={{ color: '#F7941D' }}>{m.rental_daily ? `${money(m.rental_daily)}/day` : 'Call'}</span>
        </Link>
    ));
};

export const RentalTeaser = () => {
    const t = useSiteTexts();
    return (
    <section className="border-y border-neutral-900 py-20 md:py-28" style={{ backgroundColor: '#0A0A0A' }} data-testid="home-rental-teaser">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
                <div className="label-caps mb-4" style={{ color: '#F7941D' }}>{t('home.rental.eyebrow')}</div>
                <h2 className="font-display font-black uppercase text-4xl md:text-6xl leading-[0.95]" style={{ color: '#FFFFFF' }}>
                    <Br text={t('home.rental.heading')} /><br /><span style={{ color: '#F7941D' }}>{t('home.rental.accent')}</span>
                </h2>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t('home.rental.text')}
                </p>
                <Link to="/training" className="mt-6 inline-flex items-center gap-2 label-caps text-sm" style={{ color: '#F7941D' }} data-testid="home-training-link">
                    <Award className="h-4 w-4" /> {t('home.rental.training')} <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
            <div className="lg:col-span-5 space-y-3">
                <RentalMachines />
                <Link to="/rental" className="flex items-center justify-between p-5 font-bold label-caps transition-opacity hover:opacity-90" style={{ backgroundColor: '#F7941D', color: '#0A0A0A' }} data-testid="home-rental-cta">
                    {t('home.rental.cta')} <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    </section>
    );
};

export const HomePartnerMap = () => {
    const t = useSiteTexts();
    return (
    <section className="border-t border-neutral-200 bg-neutral-50" data-testid="home-partner-map">
        <div className="container-page py-24 md:py-28">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
                <div>
                    <div className="label-caps text-brand-primary mb-4">{t('home.partners.eyebrow')}</div>
                    <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]"><Br text={t('home.partners.heading')} /></h2>
                </div>
                <Link to="/partners" className="label-caps border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-5 py-3 inline-flex items-center gap-2 transition-colors" data-testid="home-partner-program-link">
                    {t('home.partners.button')} <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
            <PartnerMapSection />
        </div>
    </section>
    );
};
